// Planning domain types aligned with the backend REST contracts.
// Sources: `erp/app/Modules/ProductionOrders` (orders, stages, lots),
// `ProductionExecution` (machines, assignments, dashboard) and
// `frontend/apps/{shopfloor,machine-operator}` type definitions.

export type ProductionOrderStatus = 'planned' | 'in_progress' | 'completed' | 'cancelled'

/** Normalized paginated result shared by the planning API layer. */
export interface PagedResult<T> {
  items: T[]
  meta: { page: number; per_page: number; total: number; total_pages: number }
}

export interface ProductionOrder {
  id: number
  mo_code: string
  order_id: number | null
  sales_order_status: string | null
  product_id: number
  product_name?: string | null
  product_sku?: string | null
  department_agent_id: number | null
  department_name?: string | null
  submitted_by_user_name?: string | null
  source_warehouse_agent_id?: number | null
  source_warehouse_name?: string | null
  destination_warehouse_agent_id?: number | null
  destination_warehouse_name?: string | null
  knitting_equipment_name?: string | null
  machine_details?: string | null
  status: ProductionOrderStatus | string
  planned_quantity: number
  produced_quantity: number
  start_date?: string | null
  end_date?: string | null
  notes?: string | null
  created_at?: string | null
}

export interface ProductionOrderFilters {
  search?: string
  status?: string
  order_id?: number | ''
  page?: number
  per_page?: number
}

/** Row of `GET /production-orders/{id}/stages`. */
export interface OrderStage {
  id: number
  production_order_id: number
  stage_name: string
  stage_code: string
  operation_sequence: number
  status: string
  planned_quantity: number
  completed_quantity: number
  rejected_quantity: number
  rework_quantity: number
  assigned_employee_name?: string | null
}

/** Row of `GET /production-orders/{id}/lots`. */
export interface ProductionLot {
  id: number
  production_order_id: number
  lot_number: string
  status: 'active' | 'completed' | 'cancelled' | string
  current_stage_code: string
  initial_quantity: number
  current_quantity: number
  color_summary?: Record<string, number> | Array<{ color: string; quantity: number }> | null
  notes?: string | null
  created_at?: string | null
}

/** Body for `POST /production-orders/{id}/lots`. */
export interface LotPayload {
  stage_code: string
  quantity: number
  color_summary?: Record<string, number> | Array<{ color: string; quantity: number }>
  notes?: string
}

/** Body for `PUT /production-orders/{id}` (controlled rescheduling). */
export interface ProductionOrderUpdatePayload {
  start_date?: string | null
  end_date?: string | null
  planned_quantity?: number
  status?: string
  notes?: string
}

export type MachineStatusValue = 'running' | 'idle' | 'down' | 'setup'

/** Row of `GET /production-execution/machines`. */
export interface Machine {
  id: number
  name: string
  status: MachineStatusValue | string
  current_order_id: number | null
  current_order_code: string | null
  current_lot_number: string | null
  counter_total: number
  counter_shift: number
  fault_active: boolean
  last_seen_at: string | null
}

/** Row of `GET /production-execution/assignments`. */
export interface MachineAssignment {
  id: number
  type: 'employee' | 'machine'
  employee_id: number | null
  employee_name: string | null
  machine_id: number | null
  machine_name: string | null
  order_id: number
  order_code: string | null
  lot_number: string | null
  stage_code: string
  stage_name: string | null
  shift: string
  status: 'active' | 'ended'
  assigned_at: string
  ended_at?: string | null
  notes?: string | null
}

export interface AssignmentFilters {
  shift?: string
  type?: 'employee' | 'machine' | ''
  order_id?: number | ''
  machine_id?: number | ''
  employee_id?: number | ''
  page?: number
  per_page?: number
}

/** Body for `POST /production-execution/assignments`. */
export interface AssignmentPayload {
  employee_id?: number | null
  machine_id?: number | null
  order_id: number
  lot_number?: string | null
  stage_code: string
  shift: string
  assigned_at?: string
  notes?: string
}

/** Aggregate of `GET /production-execution/dashboard`. */
export interface ExecutionDashboard {
  active_orders: number
  total_planned_pairs: number
  total_produced_pairs: number
  completion_rate: number
  rejected_pairs: number
  rework_pairs: number
  bottlenecks: Array<{
    stage_code: string
    stage_name: string
    order_id: number
    order_code: string
    completion: number
  }>
  assigned_employees: number
  total_employees: number
  running_machines: number
  idle_machines: number
}

// --- BOM and material availability -------------------------------------------

/** Row of `GET /bom` — one BOM version of a product. */
export interface BomVersionSummary {
  id: number
  product_id: number
  product_name: string
  product_sku: string | null
  version_number: number
  is_active: number | boolean
  materials_count: number
  operations_count: number
  created_at?: string
}

/** Material line inside a BOM operation (`GET /bom/{id}`). */
export interface BomMaterialLine {
  id: number
  material_id: number
  material_name: string
  material_sku: string | null
  material_unit: string | null
  /** Quantity per produced unit. */
  quantity: number
  loss_percentage: number
}

/** BOM detail with its operations and material lines. */
export interface BomVersionDetail extends BomVersionSummary {
  operations?: Array<{
    id: number
    operation_sequence: number
    operation_name: string
    department_agent_id?: number | null
    department_name?: string | null
    materials?: BomMaterialLine[]
  }>
}

/** Row of `GET /inventory/balances` (same shape the Warehouse app consumes). */
export interface InventoryBalanceRow {
  product_id: number
  product_name: string
  product_sku: string | null
  product_type: string | null
  product_unit: string | null
  agent_id: number
  agent_name: string
  balance: number
  last_stock_transaction_at: string | null
}

/** Shift values are free strings in the backend; these are the ones in use. */
export const SHIFT_VALUES = ['day', 'night'] as const
export type ShiftValue = (typeof SHIFT_VALUES)[number]
